// React hook wrapping Supabase's session state. Resolves once on mount
// (reads the persisted session, if any) and subscribes to auth events
// so sign-in / sign-out / recovery updates propagate immediately.
//
// Consumers get:
//   - session: the current Session or null
//   - loading: true until the first resolution completes
//   - recoveryMode: true between PASSWORD_RECOVERY and a successful
//                   updateUser({password}) — gates the recovery modal.
//                   Backed by a module-level singleton (see
//                   recoveryState.ts) so it's consistent across every
//                   component that calls useSession.
//   - signInWithEmail(email): triggers magic-link send
//   - exitRecoveryMode(): signs out and clears recoveryMode (used when
//                         the user dismisses the recovery modal without
//                         completing it; leaving the session lingering
//                         would be a UX trap)
//   - signOut(): clear session
//
// Debug: appending ?debug-recovery=1 to the URL forces recoveryMode
// on without going through a real recovery email — useful for testing
// the modal UI without burning Supabase's per-account email rate
// limit. Requires an existing signed-in session for the modal to
// render (since the modal mount-point sits inside the signed-in tree).
//
// If Supabase isn't configured, loading settles to false immediately
// with a null session. Every downstream API remains callable but no-ops.

import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { consumeCapturedRecovery, getSupabase } from './client';
import {
  getRecoveryActive,
  setRecoveryActive,
  subscribeRecoveryActive,
} from './recoveryState';

export type SignInResult = { ok: true } | { ok: false; reason: string };

// Auth-related URL params that the Supabase SDK consumes during the
// implicit/recovery/PKCE callback. After the SDK has set the session,
// we strip them so reload doesn't try to re-exchange a single-use code
// and the URL bar doesn't carry transient auth material around.
const URL_PARAMS_TO_STRIP = [
  'code',
  'access_token',
  'refresh_token',
  'expires_in',
  'expires_at',
  'token_type',
  'type',
  'provider_token',
  'provider_refresh_token',
];

// Module-level guard so the bridge runs at most once per page load,
// even though multiple useSession instances mount across the tree.
let bridgeCompleted = false;

export function useSession(): {
  session: Session | null;
  loading: boolean;
  recoveryMode: boolean;
  signInWithEmail: (email: string) => Promise<SignInResult>;
  exitRecoveryMode: () => Promise<void>;
  signOut: () => Promise<void>;
} {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(getRecoveryActive);

  useEffect(() => {
    const client = getSupabase();
    if (!client) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    // Subscribe to the recoveryMode singleton. Every useSession call
    // gets the same value, no matter which component runs the bridge.
    const unsubscribeRecovery = subscribeRecoveryActive((next) => {
      if (!cancelled) setRecoveryMode(next);
    });

    // Subscribe to auth events FIRST so events emitted during bootstrap
    // (notably SIGNED_IN from a manual setSession below) aren't missed.
    const { data: subscription } = client.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (event === 'PASSWORD_RECOVERY') {
        // PKCE-flow recovery callback (?code=...&type=recovery) — the
        // SDK fires this directly. Implicit-flow recovery URLs go
        // through the captured-recovery branch below and don't fire
        // PASSWORD_RECOVERY, so we flip the singleton there manually.
        setRecoveryActive(true);
        cleanAuthUrlParams();
      } else if (event === 'SIGNED_IN') {
        cleanAuthUrlParams();
      } else if (event === 'USER_UPDATED') {
        // updateUser({password}) fires this on success — recovery is now
        // closed and the session graduates to a normal signed-in state.
        setRecoveryActive(false);
      } else if (event === 'SIGNED_OUT') {
        setRecoveryActive(false);
      }
    });

    // Bootstrap. The first useSession instance to reach this branch
    // runs the bridge once; subsequent instances see bridgeCompleted
    // and skip straight to getSession.
    void (async () => {
      if (!bridgeCompleted) {
        bridgeCompleted = true;

        // Debug interceptor: ?debug-recovery=1 forces recoveryMode on
        // without an email round-trip. Leaves persisted session intact.
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          if (params.has('debug-recovery')) {
            setRecoveryActive(true);
            // Leave the param in the URL so reloads stay in debug
            // mode; the user removes it manually when done.
          }
        }

        const captured = consumeCapturedRecovery();
        if (captured) {
          // PKCE-configured projects don't see implicit-flow tokens for
          // anything other than recovery — magic links use ?code=… and
          // strudel doesn't run OAuth. Default to recovery unless the
          // URL explicitly says otherwise.
          const nonRecoveryTypes = new Set(['magiclink', 'invite', 'signup']);
          const isRecoveryFlow =
            !captured.type || !nonRecoveryTypes.has(captured.type);
          if (isRecoveryFlow) {
            setRecoveryActive(true);
          }
          const { error } = await client.auth.setSession({
            access_token: captured.access_token,
            refresh_token: captured.refresh_token,
          });
          if (error) {
            // Recovery token expired or malformed — back out so the
            // user isn't stuck behind a modal that can't commit.
            setRecoveryActive(false);
          }
        }
      }
      const { data } = await client.auth.getSession();
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      unsubscribeRecovery();
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signInWithEmail = useCallback(
    async (email: string): Promise<SignInResult> => {
      const client = getSupabase();
      if (!client) return { ok: false, reason: 'auth not configured' };
      const trimmed = email.trim();
      if (!trimmed || !trimmed.includes('@')) {
        return { ok: false, reason: 'enter a valid email address' };
      }
      const { error } = await client.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) return { ok: false, reason: error.message };
      return { ok: true };
    },
    [],
  );

  const signOut = useCallback(async () => {
    const client = getSupabase();
    if (!client) return;
    await client.auth.signOut();
  }, []);

  // Recovery sessions are scoped trust; if the user closes the modal
  // without setting a password, sign them out so they don't drift into
  // a "signed in but shouldn't be" state.
  const exitRecoveryMode = useCallback(async () => {
    setRecoveryActive(false);
    const client = getSupabase();
    if (!client) return;
    await client.auth.signOut();
  }, []);

  return { session, loading, recoveryMode, signInWithEmail, exitRecoveryMode, signOut };
}

function cleanAuthUrlParams(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    let dirty = false;
    for (const k of URL_PARAMS_TO_STRIP) {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        dirty = true;
      }
    }
    if (url.hash && /(?:^|&|#)(?:access_token|refresh_token|type)=/.test(url.hash)) {
      url.hash = '';
      dirty = true;
    }
    if (dirty) window.history.replaceState({}, '', url.toString());
  } catch {
    // URL constructor failures are not fatal — the user just sees
    // their existing URL bar contents until they navigate.
  }
}
